// Reusable Panel component with collapsible and draggable functionality

import { useSignal, useSignalEffect } from '@preact/signals';
import { ComponentChildren } from 'preact';
import { useRef, useEffect } from 'preact/hooks';
import {
  PanelHeader,
  CollapseButton,
  HeaderActions,
  usePanelDrag,
  loadPosition,
  clampPositionToViewport,
  getPanelRootClass,
  getPanelHeaderClass,
} from './panel';

interface PanelProps {
  /** Unique ID for the panel container */
  id?: string;
  /** Title displayed in the panel header */
  title: string;
  /** Whether the panel starts collapsed */
  initialCollapsed?: boolean;
  /** Optional additional header content (buttons, etc.) */
  headerActions?: ComponentChildren;
  /** Panel body content */
  children: ComponentChildren;
}

/**
 * Reusable Panel component with collapsible header and drag functionality
 */
export function Panel({
  id,
  title,
  initialCollapsed = false,
  headerActions,
  children,
}: PanelProps) {
  const collapsed = useSignal(initialCollapsed);
  const position = useSignal<{ x: number; y: number } | null>(null);
  const isDragging = useSignal(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const { handleMouseDown, handleMouseMove, handleMouseUp } = usePanelDrag(
    panelRef,
    isDragging,
    position,
    id
  );

  // Load position from localStorage on mount
  useEffect(() => {
    if (id) {
      const saved = loadPosition(id);
      if (saved) {
        position.value = saved;
      }
    }
  }, [id]);

  // Apply position styles
  useSignalEffect(() => {
    if (panelRef.current && position.value) {
      panelRef.current.style.left = `${position.value.x}px`;
      panelRef.current.style.top = `${position.value.y}px`;
      // Override CSS bottom/right when position is controlled
      panelRef.current.style.bottom = 'auto';
      panelRef.current.style.right = 'auto';
    }
  });

  // Add global mouse listeners for dragging
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (isDragging.value) handleMouseMove(e);
    };
    const onUp = () => {
      if (isDragging.value) handleMouseUp();
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);

    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  // Clamp saved position on window resize so the panel stays visible
  useEffect(() => {
    const handleResize = () => clampPositionToViewport(panelRef, position, id);

    // Run once to ensure any loaded positions are clamped initially
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [id]);

  return (
    <div
      ref={panelRef}
      id={id}
      data-testid={id}
      className={getPanelRootClass(id)}
      data-collapsed={collapsed.value ? 'true' : 'false'}
      data-dragging={isDragging.value ? 'true' : 'false'}
    >
      <PanelHeader
        className={getPanelHeaderClass(id)}
        onMouseDown={handleMouseDown}
        isDragging={isDragging.value}
      >
        <span>{title}</span>
        <HeaderActions>
          {headerActions}
          <CollapseButton
            collapsed={collapsed.value}
            onClick={() => (collapsed.value = !collapsed.value)}
            panelId={id}
          />
        </HeaderActions>
      </PanelHeader>
      {!collapsed.value && <div data-testid="panel-body">{children}</div>}
    </div>
  );
}
